import dayjs from "dayjs";
import { ColorizeParams, ConsoleColours, DefinedToken, HMS, Token } from "./interface";
import { Buffer } from "buffer";

/**
 *
 * @param {number} diff A number representing the difference between two dates in milliseconds
 * @returns {HMS} An object containing the hours, minutes and seconds of the difference
 * @description Converts a number representing the difference between two dates in milliseconds to an object containing the hours, minutes and seconds of the difference
 *
 * @example
 * const diff = 1000 * 60 * 60 * 2 + 1000 * 60 * 30 + 1000 * 10;
 * const { H, M, S } = convertDiffToHMS(diff);
 * console.log(H, M, S); // 2 30 10
 *
 */

export const convertDiffToHMS = (diff: number): HMS => {
  // get hours, minutes and seconds from a time difference
  // return an object with the hours, minutes and seconds
  // use the Date object to get the hours, minutes and seconds
  // In UTC time, the time difference is the time difference between the current time and the time the access token was requested

  const date = new Date(diff);
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();

  return {
    H: hours,
    M: minutes,
    S: seconds,
    // H: Math.floor(diff / 1000 / 3600),
    // M: Math.floor(diff / 1000 / 60),
    // S: Math.floor(diff / 1000 - (diff / 1000 / 60) * 60),
  };
};

/**
 *
 * @returns {string} A string representing the date and time in the format YYYY-MM-DD HH:mm:ss
 * @description Returns a string representing the date and time in the format YYYY-MM-DD HH:mm:ss
 *
 * @example
 * const date = getDateTime();
 * console.log(date); // 2021-05-20 10:00:00
 *
 */
const getDateTime = () => {
  const stamp = new Date();
  const time = stamp.toLocaleTimeString("en-GB");
  const date = stamp.toLocaleDateString("en-GB");
  return `${date} ${time}`;
};

/**
 *
 * @param {string} namespace A string representing the namespace of the log
 * @param {string} scope A string representing the scope of the log
 * @param {string} message A string representing the message of the log
 * @param {(object|undefined)} obj An object representing the object of the log, if any
 * @returns {void}
 *
 * @description Logs a message to the console
 *
 * @example
 * const obj = { a: 1, b: 2 };
 * simpleLogger("namespace", "error", "message", obj);
 */

export const simpleLogger = (namespace: string, scope: string, message: string, obj?: any) => {
  console.group(`${namespace} [${scope.toUpperCase()}] [${getDateTime()}]`);
  if (scope === "info") {
    console.log(`${message}`);
  } else if (scope === "error") {
    console.error(`${message}`);
  } else if (scope === "warn") {
    console.warn(`${message}`);
  } else if (scope === "debug") {
    console.debug(`${message}`);
  }
  if (obj) {
    console.log(obj);
  }
  console.groupEnd();
};

/**
 * @param {any} obj An object representing the object to be processed
 * @returns {any} An object representing the processed object
 * @description Processes an object by trimming all the string values recursively
 *
 * @example
 * const obj = { a: " 1 ", b: " 2 " };
 * const processedObj = trimAll(obj);
 * console.log(processedObj); // { a: "1", b: "2" }
 */

export const trimAll = (obj: any) => {
  // if array, loop through and trim each object
  // if object, loop through and trim each object
  // if string, trim
  // if number, return
  // if boolean, return
  // if null, return
  // if undefined, return

  if (typeof obj === "undefined" || obj === null || typeof obj === "number") return obj;
  else if (typeof obj === "string") {
    return obj.trim();
  } else if (Array.isArray(obj)) {
    obj = obj.map((item) => trimAll(item));
  } else if (typeof obj === "object") {
    Object.keys(obj).forEach((key) => {
      obj[key] = trimAll(obj[key]);
    });
  }
  return obj;
};

/**
 * @param {HMS} hms An object representing the hours, minutes and seconds
 * @returns {string} A string representing the hours, minutes and seconds in human readable format
 * @description Converts an object representing the hours, minutes and seconds to a string representing the hours, minutes and seconds in human readable format
 *
 * @example
 * const hms = { H: 2, M: 30, S: 10 };
 * const time = convertHMSToTime(hms);
 * console.log(time); // 2 hours, 30 minutes and 10 seconds
 */
export const convertHMSToTime = (hms: HMS) => {
  return `${hms.H} hours, ${hms.M} minutes and ${hms.S} seconds`;
};

/**
 * @param {ColorizeParams} params An object representing the parameters for the colorize function
 * @returns {string} A string representing the colorized and formatted message
 * @description Returns a string representing the colorized and formatted message
 *
 * @example
 * const text = "text";
 * const colorizedText = colorize(text, "red", true);
 * console.log(colorizedText); // \x1b[31m\x1b[1mtext\x1b[0m
 */
export const formatConsoleLog = ({
  colour,
  text,
  background,
  blink,
  bold,
  hidden,
  inverse,
  strikethrough,
  underline,
}: ColorizeParams): string => {
  const colours: {
    [key in ConsoleColours]: string;
  } = {
    gray: "\x1b[90m",
    grey: "\x1b[90m",
    red: "\x1b[31m",
    green: "\x1b[32m",
    yellow: "\x1b[33m",
    blue: "\x1b[34m",
    magenta: "\x1b[35m",
    cyan: "\x1b[36m",
    white: "\x1b[37m",
    black: "\x1b[30m",
  };
  const colorCode = colours[colour];
  const boldCode = bold ? "\x1b[1m" : "";
  const underlineCode = underline ? "\x1b[4m" : "";
  const blinkCode = blink ? "\x1b[5m" : "";
  const inverseCode = inverse ? "\x1b[7m" : "";
  const hiddenCode = hidden ? "\x1b[8m" : "";
  const strikethroughCode = strikethrough ? "\x1b[9m" : "";
  const backgroundCode = background ? `\x1b[4${background}m` : "";
  const resetCode = "\x1b[0m";

  return `${colorCode}${boldCode}${underlineCode}${blinkCode}${inverseCode}${hiddenCode}${strikethroughCode}${backgroundCode}${text}${resetCode}`;
};

/**
 * @param {( WithImplicitCoercion<ArrayBuffer | SharedArrayBuffer>)} data An object representing the data to be encoded to base64
 * @returns {string} A string representing the base64 encoded data
 * @description Returns a string representing the base64 encoded data
 *
 * @example
 * const data = "data";
 * const base64Data = encodeToBase64(data);
 * console.log(base64Data); // ZGF0YQ==
 */
export const encodeToBase64 = (data: WithImplicitCoercion<ArrayBuffer | SharedArrayBuffer>): string => {
  return Buffer.from(data).toString("base64");
};

/**
 * @param {string} data A string representing the base64 encoded data
 * @returns {string} A string representing the decoded data
 * @description Returns a string representing the decoded data
 *
 * @example
 * const data = "ZGF0YQ==";
 * const decodedData = decodeFromBase64(data);
 * console.log(decodedData); // data
 */
export const decodeFromBase64 = (data: string): string => {
  return Buffer.from(data, "base64").toString("utf8");
};

/**
 * @param {string} token A string representing the token to be decoded from JWT
 * @returns {Token} An object representing the decoded token
 * @description Returns an object representing the decoded token
 *
 * @example
 * const toke = "eyJhb"
 * const decodedToken = decodeJWT(token);
 * console.log(decodedToken); // { id: "123", name: "John Doe" }
 */

export const decodeJwt = (token?: string): Token => {
  if (!token) {
    return undefined;
  }

  const [, base64] = token.split(".");
  const decoded = decodeFromBase64(base64);
  const data = JSON.parse(decoded) as DefinedToken;

  // correct for the fact that the token expiry is in seconds and not milliseconds
  if (data.exp < 100000000000) {
    data.exp = data.exp * 1000;
  }

  // correct for the fact that the token issued at is in seconds and not milliseconds
  if (data.iat < 100000000000) {
    data.iat = data.iat * 1000;
  }

  return data;
};

/**
 * @param {DefinedToken} definedToken An object representing the token to be encoded to JWT
 * @returns {string} A string representing the encoded token
 * @description Returns a string representing the encoded token
 *
 * @example
 * const token
 * const encodedToken = encodeJWT(token);
 * console.log(encodedToken); // eyJhb
 */
export const encodeJwt = (definedToken: DefinedToken): string => {
  const header = {
    alg: "HS256",
    typ: "JWT",
  };

  definedToken.iat = dayjs(definedToken.iat).valueOf();
  definedToken.exp = dayjs(definedToken.exp).valueOf();

  const stringifiedHeader = JSON.stringify(header);
  const encodedHeader = btoa(stringifiedHeader);

  const stringifiedData = JSON.stringify(definedToken);
  const encodedData = btoa(stringifiedData);

  const token = `${encodedHeader}.${encodedData}`;

  return token;
};

/**
 * @param {Token} token An object representing the token to be checked for type
 * @returns {string} A string representing the type of the token
 * @description Returns a string representing the type of the token
 *
 * @example
 * const token
 * const tokenType = getTokenType(token);
 * console.log(tokenType); // "access"
 */

export const getTokenType = (token: Token): "access" | "refresh" | "unknown" => {
  if (!token) {
    return "unknown";
  }

  if (isAccessToken(token)) {
    return "access";
  }

  if (isRefreshToken(token)) {
    return "refresh";
  }

  return "unknown";
};

/**
 *
 * @param {Token} token A token to check if it is an access token
 * @returns {boolean} true if the token is an access token, false otherwise
 * @description An access token is a token that has a property called "alr" and a property called "alw"
 *
 * @example
 *
 * const token = {
 * alr: true,
 * alw: true,
 * aud: "some-audience",
 * exp: 1620000000,
 * iat: 1620000000,
 * iss: "some-issuer",
 * sub: "some-subject"
 * }
 *
 * isAccessToken(token) // true
 *
 * const token = {
 * alr: true,
 * aud: "some-audience",
 * exp: 1620000000,
 * iat: 1620000000,
 * iss: "some-issuer",
 * sub: "some-subject"
 * }
 *
 * isAccessToken(token) // false
 *
 */
export const isAccessToken = (token: Token): boolean => {
  if (!token) return false;
  return token.hasOwnProperty("aud") && token.hasOwnProperty("nbf");
};

/**
 *
 * @param {Token} token A token to check if it is a refresh token
 * @returns {boolean} true if the token is a refresh token, false otherwise
 * @description A refresh token is a token that has a property called "sid"
 *
 * @example
 *
 * const token = {
 * sid: "some",
 * alr: true,
 * alw: true,
 * aud: "some-audience",
 * exp: 1620000000,
 * iat: 1620000000,
 * iss: "some-issuer",
 * sub: "some-subject"
 * }
 *
 * isRefreshToken(token) // true
 *
 * const token = {
 * alr: true,
 * alw: true,
 * aud: "some-audience",
 * exp: 1620000000,
 * iat: 1620000000,
 * iss: "some-issuer",
 * sub: "some-subject"
 * }
 *
 * isRefreshToken(token) // false
 *
 */

export const isRefreshToken = (token: Token): boolean => {
  if (!token) return false;
  return !token.hasOwnProperty("aud");
};

/**
 *
 * @param {Token} token A token to check if it expired
 *
 * @returns {boolean} true if the token is expired or not defined, false otherwise
 *
 * @description A token is expired if the current time is greater than the expiry time of the token or if the token is not defined
 *
 * @example
 *
 * const token = {
 * alr: true,
 * alw: true,
 * aud: "some-audience",
 * exp: 1620000000,
 * iat: 1620000000,
 * iss: "some-issuer",
 * sub: "some-subject"
 * }
 *
 * isTokenExpired(token) // false
 *
 * const token = {
 * alr: true,
 * alw: true,
 * aud: "some-audience",
 * exp: 1620000000,
 * iat: 1620000000,
 * iss: "some-issuer",
 * sub: "some-subject"
 * }
 *
 * isTokenExpired(token) // false
 */

export const isTokenExpired = (token: Token): boolean => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return true;
  return !!expiry && expiry.isBefore(dayjs());
};

/**
 *
 * @param {Token} token A token to get the expiry date of
 *
 * @returns {(dayjs.Dayjs | undefined)} The expiry date of the token as a dayjs object or undefined if the token is not defined
 *
 * @description The expiry date of a token is the date when the token expires
 *
 * @example
 *
 * const token = {
 * alr: true,
 * alw: true,
 * aud: "some-audience",
 * exp: 1620000000,
 * iat: 1620000000,
 * iss: "some-issuer",
 * sub: "some-subject"
 * }
 *
 * getTokenExpiry(token) // dayjs object
 *
 */

export const getTokenExpiry = (token: Token): dayjs.Dayjs | undefined => {
  if (!token || !token.exp) return undefined;
  if (isRefreshToken(token)) {
    return dayjs(token.exp);
  }
  return dayjs(token.exp);
};

/**
 *
 * @param {Token} token A token to get the issued date of
 *
 * @returns {(dayjs.Dayjs | undefined)} The issued date of the token as a dayjs object or undefined if the token is not defined
 *
 * @description The issued date of a token is the date when the token was issued by the server
 *
 * @example
 *
 * const token = {
 * alr: true,
 * alw: true,
 * aud: "some-audience",
 * exp: 1620000000,
 * iat: 1620000000,
 * iss: "some-issuer",
 * sub: "some-subject"
 * }
 *
 * getTokenIssuedDate(token) // dayjs object
 */

export const getTokenIssuedDate = (token: Token): dayjs.Dayjs | undefined => {
  if (!token || !token.iat) return undefined;
  return dayjs(token.iat);
};

/**
 *
 * @param {Token} token A token to check if it is about to expire
 *
 * @returns {boolean} true if the token is about to expire, false otherwise
 *
 * @description A token is about to expire if the current time is greater than the expiry time of the token minus the refresh token expiry time
 *
 * @example
 *
 * const token = {
 * alr: true,
 * alw: true,
 * aud: "some-audience",
 * exp: 1620000000,
 * iat: 1620000000,
 * iss: "some-issuer",
 * sub: "some-subject"
 * }
 *
 * isTokenAboutToExpire(token) // false
 */

export const isTokenAboutToExpire = (token: Token): boolean => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return true;

  // return if token is access token and expired by 1 minute
  const tokenType = getTokenType(token);
  if (tokenType === "access") {
    return dayjs().isAfter(expiry.subtract(1, "minutes"));
  }

  if (tokenType === "refresh") {
    return dayjs().isAfter(expiry.subtract(1, "minutes"));
  }

  return true;
};

/**
 * Checks if the token can be gracefully refreshed if it is about to expire in the next 5 seconds
 * @param {Token} token A token to check if it can be gracefully refreshed
 * @returns {boolean} true if the token can be gracefully refreshed, false otherwise
 * @description A token can be gracefully refreshed if it is a refresh token and it is about to expire in the next 5 seconds
 * @example
 *
 * const token = {
 * alr: true,
 * alw: true,
 * aud: "some-audience",
 * exp: 1620000000,
 * iat: 1620000000,
 * iss: "some-issuer",
 * sub: "some-subject"
 * }
 *
 * canTokenBeGracefullyRefreshed(token) // false
 */

export const canTokenBeGracefullyRefreshed = (token: Token): boolean => {
  const tokenType = getTokenType(token);
  if (tokenType !== "access") return false;
  const expiry = getTokenExpiry(token);
  if (!expiry) return false;
  if (expiry.subtract(5, "seconds").diff(dayjs(), "seconds") <= 0) {
    return true;
  }
  return false;
};

/**
 *
 * @param {Token} token A token which has been decoded from a JWT or undefined
 * @returns {HMS} HMS object with hours, minutes and seconds until the token expires
 * @description Returns the time until the token expires as an HMS object
 *
 * @example
 *
 * const refreshToken = decodeJwt(refreshTokenString);
 * const timeUntilTokenExpires = timeUntilTokenExpires(refreshToken);
 *
 * console.log(timeUntilTokenExpires.hours); // 10
 * console.log(timeUntilTokenExpires.minutes); // 59
 * console.log(timeUntilTokenExpires.seconds); // 59
 *
 * const accessToken = decodeJwt(accessTokenString);
 * const timeUntilTokenExpires = timeUntilTokenExpires(accessToken);
 *
 * console.log(timeUntilTokenExpires.hours); // 0
 * console.log(timeUntilTokenExpires.minutes); // 59
 * console.log(timeUntilTokenExpires.seconds); // 59
 */

export const timeUntilTokenExpires = (token: Token): HMS => {
  const expiry = getTokenExpiry(token);
  if (!expiry) return convertDiffToHMS(0);
  return convertDiffToHMS(expiry.diff(dayjs(), "milliseconds"));
};

/**
 *
 * @param {Token} token A token which has been decoded from a JWT or undefined
 * @returns True if the token is not expired, false otherwise
 * @description Checks if the token is not expired
 *
 * @example
 *
 * const refreshToken = decodeJwt(refreshTokenString);
 *
 * const isValidToken = hasValidToken(refreshToken);
 *
 * console.log(isValid); // true
 */

export const hasValidToken = (token: Token): boolean => {
  return !isTokenExpired(token);
};
